"use client";

import { useEffect, useMemo, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const livingCopy =
  "Simply opening an IFZA company won't automatically cancel your tax bills back home. Outside of tax, the reality of living here is what seals the deal. You get incredible global flight connections, modern infrastructure, and a safe, high-end lifestyle if you decide to make the move.";

export const LivingInUaeSection = () => {
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const words = useMemo(() => livingCopy.split(" "), []);

  useEffect(() => {
    const paragraph = paragraphRef.current;
    if (!paragraph) return;

    gsap.registerPlugin(ScrollTrigger);

    const context = gsap.context(() => {
      gsap.fromTo(
        paragraph.querySelectorAll(".living-word"),
        { opacity: 0.32 },
        {
          opacity: 1,
          ease: "none",
          stagger: 0.08,
          scrollTrigger: {
            trigger: paragraph,
            start: "top bottom-=12%",
            end: "bottom bottom-=12%",
            scrub: 1,
          },
        },
      );
    }, paragraph);

    // Recalculate trigger positions after one frame so that:
    // (a) the -76px navbar offset on <main> is already applied in CSS,
    // (b) Lenis has had time to initialise and take over scroll,
    // (c) any late-loading fonts that shift layout are accounted for.
    const rafId = requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });

    return () => {
      cancelAnimationFrame(rafId);
      context.revert();
    };
  }, []);

  return (
    <section
      aria-label="Living in the UAE"
      className="relative isolate flex w-full items-center justify-center overflow-hidden bg-black px-6 py-[70px] sm:px-10 lg:min-h-[500px] lg:px-12"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-[418px] top-[188.39px] h-[500px] w-[500px] rounded-full bg-[#6FA6FF] blur-[162.2px]"
      />
      <div className="relative z-10 mx-auto flex w-full max-w-[1113px] flex-col items-center gap-5 text-center">
        <p
          ref={paragraphRef}
          aria-label={livingCopy}
          className="w-full font-suisse-intl text-2xl font-normal leading-10 tracking-[0.24px] text-[#a0a4b3] sm:text-3xl sm:leading-[52px] lg:text-[40px] lg:tracking-[0.4px]"
        >
          {words.map((word, index) => (
            <span
              className="living-word inline-block text-white"
              aria-hidden="true"
              key={`${word}-${index}`}
            >
              {word}
              {index < words.length - 1 ? " " : ""}
            </span>
          ))}
        </p>
        <div aria-hidden="true" className="relative h-[55px] w-[58px]" />
      </div>
    </section>
  );
};
